// =============================================================================
// runPreflight — what is still open on a run when someone taps Complete Run
// (plan §2D item 6). A WARNING, never a block: the operator sees the list and
// completes anyway if that is right. Before this, Complete validated nothing
// about the run's content.
//
// The five things it looks at: linked deliveries with bins still to tip,
// pallets awaiting a weight, no reject evidence on a run that moved real
// fruit, lots (tipped pallets / deliveries) with fruit in and nothing out,
// and pallets still awaiting placement (status IN_PRODUCTION). That last one
// matters because a completed run's pallets drop off the Pallets page's
// by-run list (PACKING_OPEN_STATUSES), so Complete Run is the last moment
// anyone is told a pallet was never put in cold or the loading area.
// A read that fails is REPORTED as unchecked, never treated as clean.
// =============================================================================
import type { SupabaseClient } from '@supabase/supabase-js'
import { rejectCapture, type PackoutRun } from '@/lib/growerPackout'

export interface PreflightFacts {
  untippedDeliveries: { ref: string; remaining: number }[]
  awaitingWeight:     number
  noRejectEvidence:   boolean
  lotsWithoutOutput:  string[]
  /** Pallets on the run still IN_PRODUCTION: built, never placed. */
  awaitingPlacement?: number
  unchecked:          string[]
}

/** The line for a run that moved real fruit with no rejects or waste logged. */
export const NO_REJECTS_ISSUE = 'No rejects or waste logged on a run that moved real fruit — the grower-facing packout cannot be stated'

/** The operator's list, one line per open item, empty when the run is clean. */
export function preflightIssues(f: PreflightFacts): string[] {
  const out: string[] = []
  for (const d of f.untippedDeliveries) out.push(`${d.ref}: ${d.remaining} bin${d.remaining === 1 ? '' : 's'} still to tip`)
  if (f.awaitingWeight > 0) out.push(`${f.awaitingWeight} pallet${f.awaitingWeight === 1 ? '' : 's'} still awaiting a weight`)
  if (f.noRejectEvidence) out.push(NO_REJECTS_ISSUE)
  for (const lot of f.lotsWithoutOutput) out.push(`${lot}: tipped in, nothing packed out yet`)
  const unplaced = f.awaitingPlacement ?? 0
  // Ruled 8 Oct: names no page. The proposal's "from the Coldroom" waits on
  // F05 UF-01, which is not built, and "on Pallets" read as the only route.
  if (unplaced > 0) out.push(`${unplaced} pallet${unplaced === 1 ? '' : 's'} still awaiting placement — place ${unplaced === 1 ? 'it' : 'them'} before you complete the run`)
  for (const u of f.unchecked) out.push(`Could not check ${u} — decide with that in mind`)
  return out
}

/** The body of the "Complete … with open items?" confirm: the list, then what
 *  completing means. Completing is never blocked — but with no rejects logged
 *  it is not free either. Rejects lists open runs only (PACKING_OPEN_STATUSES),
 *  so a run completed without them cannot take them, and its grower packout
 *  reads "rejects not captured" everywhere, until an admin reopens it. "Nothing
 *  here is a block" said none of that (F02 D5). */
export function preflightConfirmMessage(issues: string[]): string {
  const tail = issues.includes(NO_REJECTS_ISSUE)
    ? 'You can complete anyway, but rejects can only be logged while the run is open. '
      + 'Once it is complete, no rejects can be added and the grower\'s packout shows '
      + '"rejects not captured" until an admin reopens the run (Reopen Run (admin)).'
    : 'You can complete anyway; nothing here is a block.'
  return issues.map(i => `• ${i}`).join('\n') + '\n\n' + tail
}

type Db = Pick<SupabaseClient, 'from'>

async function guarded<T>(label: string, unchecked: string[], read: () => Promise<{ data: T | null; error: { message: string } | null }>, empty: T): Promise<T> {
  try {
    const { data, error } = await read()
    if (error) throw error
    return data ?? empty
  } catch {
    unchecked.push(label)
    return empty
  }
}

/** Gather the facts for one run. Each read is independent; a failure marks that check unchecked. */
export async function fetchRunPreflight(db: Db, run: PackoutRun & { run_id: string }): Promise<string[]> {
  const unchecked: string[] = []
  const [deliveries, awaiting, lots, unplaced] = await Promise.all([
    guarded('the linked deliveries', unchecked, () =>
      // One run links a handful of deliveries; the cap is far above any real run.
      db.from('run_deliveries').select('deliveries(delivery_note_number, client_ref_no, bins_remaining, grower)').eq('run_id', run.run_id).limit(500) as never,
      [] as { deliveries: unknown }[]),
    guarded('the pallets awaiting a weight', unchecked, () =>
      db.from('pallets').select('pallet_id').eq('run_id', run.run_id).eq('awaiting_weight', true).is('deleted_at', null).limit(1000) as never,
      [] as { pallet_id: string }[]),
    guarded('the lots', unchecked, () =>
      db.from('run_lot_packout').select('lot_key, source_pallet_id, delivery_note_number, client_ref_no, kg_in, cartons_out').eq('run_id', run.run_id).limit(1000) as never,
      [] as { lot_key: string; source_pallet_id: string | null; delivery_note_number: string | null; client_ref_no: string | null; kg_in: number; cartons_out: number }[]),
    guarded('the pallets awaiting placement', unchecked, () =>
      db.from('pallets').select('pallet_id').eq('run_id', run.run_id).eq('status', 'IN_PRODUCTION').is('deleted_at', null).limit(1000) as never,
      [] as { pallet_id: string }[]),
  ])
  const untipped: PreflightFacts['untippedDeliveries'] = []
  for (const row of deliveries) {
    const d = (Array.isArray(row.deliveries) ? row.deliveries[0] : row.deliveries) as { delivery_note_number: string | null; client_ref_no: string | null; bins_remaining: number; grower: string } | null
    if (d && d.bins_remaining > 0) untipped.push({ ref: d.delivery_note_number ? `DN ${d.delivery_note_number}` : (d.client_ref_no ? `Ref ${d.client_ref_no}` : d.grower), remaining: d.bins_remaining })
  }
  const lotsWithoutOutput = lots
    .filter(l => Number(l.kg_in) > 0 && Number(l.cartons_out) === 0)
    .map(l => l.source_pallet_id ?? (l.delivery_note_number ? `DN ${l.delivery_note_number}` : (l.client_ref_no ? `Ref ${l.client_ref_no}` : l.lot_key)))
  return preflightIssues({
    untippedDeliveries: untipped,
    awaitingWeight:     awaiting.length,
    noRejectEvidence:   rejectCapture(run) === 'NOT_CAPTURED',
    lotsWithoutOutput,
    awaitingPlacement:  unplaced.length,
    unchecked,
  })
}
